import { Activity, WifiOff, PlayCircle, AlertCircle } from 'lucide-react';
import { ScannerDevice } from '../../../types/scanner';

interface DeviceListProps {
  devices: ScannerDevice[];
  onNewScan: (device: ScannerDevice) => void;
}

export default function DeviceList({ devices, onNewScan }: DeviceListProps) {
  if (devices.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <WifiOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No scanner devices registered</p>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <Activity className="w-5 h-5 text-green-600" />;
      case 'scanning':
        return <PlayCircle className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <WifiOff className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string, isOnline: boolean) => {
    if (status === 'scanning') {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
          Scanning
        </span>
      );
    }
    if (isOnline) {
      return (
        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
          Online
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
        Offline
      </span>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {devices.map((device) => (
        <div
          key={device.id}
          className="bg-white rounded-lg shadow hover:shadow-lg transition p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon(device.status)}
              <div>
                <h3 className="font-semibold text-gray-900">{device.name}</h3>
                <p className="text-sm text-gray-500">{device.machine_id}</p>
              </div>
            </div>
            {getStatusBadge(device.status, device.is_online)}
          </div>

          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex justify-between">
              <span>Location:</span>
              <span className="font-medium">{device.location || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>Address:</span>
              <span className="font-medium">{device.ip_address}:{device.port}</span>
            </div>
          </div>

          <button
            onClick={() => onNewScan(device)}
            disabled={device.status === 'scanning' || !device.is_online}
            className={`w-full py-2 px-4 rounded-lg font-medium transition ${
              device.status === 'scanning' || !device.is_online
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {device.status === 'scanning' ? 'Scanning...' : 'Start New Scan'}
          </button>
        </div>
      ))}
    </div>
  );
}
